import React, { useEffect, useState, useRef } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);
  const navLinks = [{
    name: 'Home',
    to: '/'
  }, {
    name: 'About',
    to: '/about'
  }, {
    name: 'Projects',
    to: '/projects'
  }, {
    name: 'Bids & Tenders',
    to: '/bids'
  }];
  const navLinkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-au-green focus:ring-offset-2 rounded-sm ${isActive ? 'text-au-green' : 'text-gray-800 hover:text-au-green'}`;
  return <header className="bg-white sticky top-0 z-50 shadow-sm border-b border-gray-100" data-id="element-188">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center" data-id="element-189">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-3" aria-label="African Union Commission — Home" data-id="element-190">
          <div className="w-12 h-12 relative flex-shrink-0" data-id="element-191">
            <svg viewBox="0 0 100 100" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-192">
              <circle cx="50" cy="50" r="48" stroke="#f4c430" strokeWidth="2" fill="#fff" data-id="element-193" />
              <circle cx="50" cy="50" r="40" stroke="#15703c" strokeWidth="1" strokeDasharray="4 4" fill="none" data-id="element-194" />
              <path d="M35 30C45 20 60 20 70 30C80 40 80 55 70 65C60 75 45 75 35 65C25 55 25 40 35 30Z" fill="#f4c430" opacity="0.3" data-id="element-195" />
              <path d="M40 35C48 28 58 28 65 35C72 42 72 52 65 58C58 65 48 65 40 58C32 52 32 42 40 35Z" fill="#15703c" data-id="element-196" />
            </svg>
          </div>
          <div className="flex flex-col" data-id="element-197">
            <span className="text-au-red font-serif font-bold text-xl leading-tight" data-id="element-198">
              African Union
            </span>
            <span className="text-gray-600 text-sm font-medium leading-tight" data-id="element-199">
              Commission
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-8" aria-label="Main navigation" data-id="element-200">
          {navLinks.map(link => <NavLink key={link.name} to={link.to} end={link.to === '/'} className={navLinkClass} data-id="element-201">
              {link.name}
            </NavLink>)}
          <div className="relative" ref={dropdownRef} data-id="element-202">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} aria-expanded={isDropdownOpen} aria-haspopup="true" className="flex items-center space-x-1 text-gray-800 hover:text-au-green font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-au-green focus:ring-offset-2 rounded-sm" data-id="element-203">
              <span data-id="element-204">Procurement</span>
              <ChevronDown size={14} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-205" />
            </button>
            <div role="menu" className={`absolute top-full left-0 mt-2 w-48 bg-white shadow-lg border border-gray-100 rounded-md transition-all duration-200 ${isDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`} data-id="element-206">
              <Link to="/procurement" role="menuitem" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-au-green focus:bg-gray-50 focus:text-au-green focus:outline-none" onClick={() => setIsDropdownOpen(false)} data-id="element-207">
                Guidelines
              </Link>
              <Link to="/procurement/supplier-registration" role="menuitem" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-au-green focus:bg-gray-50 focus:text-au-green focus:outline-none" onClick={() => setIsDropdownOpen(false)} data-id="element-208">
                Supplier Registration
              </Link>
              <Link to="/procurement" role="menuitem" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-au-green focus:bg-gray-50 focus:text-au-green focus:outline-none" onClick={() => setIsDropdownOpen(false)} data-id="element-209">
                Awarded Contracts
              </Link>
            </div>
          </div>
          <NavLink to="/contact" className={navLinkClass} data-id="element-210">
            Contact
          </NavLink>
        </nav>

        {/* Mobile Menu Toggle */}
        <button className="md:hidden text-gray-800 focus:outline-none focus:ring-2 focus:ring-au-green rounded-sm" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-expanded={isMobileMenuOpen} aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'} data-id="element-211">
          {isMobileMenuOpen ? <X size={24} data-id="element-212" /> : <Menu size={24} data-id="element-213" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {isMobileMenuOpen && <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-4 shadow-inner" data-id="element-214">
          {navLinks.map(link => <NavLink key={link.name} to={link.to} end={link.to === '/'} className="block text-gray-800 font-medium hover:text-au-green" onClick={() => setIsMobileMenuOpen(false)} data-id="element-215">
              {link.name}
            </NavLink>)}
          <div className="space-y-2" data-id="element-216">
            <span className="block text-gray-800 font-medium" data-id="element-217">Procurement</span>
            <div className="pl-4 space-y-2 border-l-2 border-gray-100" data-id="element-218">
              <Link to="/procurement" className="block text-sm text-gray-600" onClick={() => setIsMobileMenuOpen(false)} data-id="element-219">
                Guidelines
              </Link>
              <Link to="/procurement/supplier-registration" className="block text-sm text-gray-600" onClick={() => setIsMobileMenuOpen(false)} data-id="element-220">
                Supplier Registration
              </Link>
            </div>
          </div>
          <NavLink to="/contact" className="block text-gray-800 font-medium hover:text-au-green" onClick={() => setIsMobileMenuOpen(false)} data-id="element-221">
            Contact
          </NavLink>
        </div>}
    </header>;
}